import React, { useState, useEffect } from 'react';
import { FiCloud,FiAlertTriangle, FiUpload, FiDownload, FiClock, FiFileText, FiCheckCircle, FiPackage } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../pages/app/protected/firebase';
import { collection, getDocs, query, where, writeBatch, doc, serverTimestamp, orderBy, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

const Backup = () => {
  const { companyId, user } = useAuth();
  const [activeTab, setActiveTab] = useState('export'); // 'export' or 'import'
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState('');
  const [exportError, setExportError] = useState('');
  const [lastExportTime, setLastExportTime] = useState('');
  const [exportType, setExportType] = useState('products'); // 'products', 'journal', 'moneyIn', 'moneyOut', 'bundles' or 'all'
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importType, setImportType] = useState('products'); // 'products', 'journal', 'moneyIn', 'moneyOut', 'bundles'
  const [importStatus, setImportStatus] = useState({ success: null, message: '' });
  const [validationErrors, setValidationErrors] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const fileInputRef = React.createRef(null);

  const downloadCSV = (data, headers, filename) => {
    try {
      const csvContent = [
        headers.join(','),
        ...data.map(row => 
          row.map(field => 
            `"${String(field || '').replace(/"/g, '""')}"`
          ).join(',')
        )
      ].join('\n');
      
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('Error generating CSV:', error);
      setExportError('Failed to generate CSV file. Please try again.');
    }
  };

  const exportData = async () => {
    if (!companyId) {
      setExportError('Company ID is missing. Please sign in again.');
      return;
    }
    
    setIsExporting(true);
    setExportError('');
    setExportSuccess('');
    
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      // Process exports in sequence to avoid memory issues
      if (exportType === 'products' || exportType === 'all') {
        await exportProducts(timestamp);
      }
      if (exportType === 'bundles' || exportType === 'all') {
        await exportBundles(timestamp);
      }
      
      if (exportType === 'journal' || exportType === 'all') {
        await exportJournal(timestamp);
      }
      
      if (exportType === 'moneyIn' || exportType === 'all') {
        await exportMoneyIn(timestamp);
      }
      
      if (exportType === 'moneyOut' || exportType === 'all') {
        await exportMoneyOut(timestamp);
      }
      
      // Only show success if we didn't encounter any errors
      if (!exportError) {
        setLastExportTime(new Date().toLocaleString());
        setExportSuccess(`Successfully exported ${exportType === 'all' ? 'all data' : exportType}`);
      }
      
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(error.message || 'An unexpected error occurred during export');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Helper function to export products
  const exportProducts = async (timestamp) => {
    try {
      const productsRef = collection(db, 'companies', companyId, 'inventory', 'data', 'products');
      const productsSnapshot = await getDocs(productsRef);
      
      if (productsSnapshot.empty) {
        console.log('No products found to export');
        return;
      }
      
      const products = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const productHeaders = [
        'Record Type', // product or variant
        'Parent SKU',
        'SKU',
        'Product Name',
        'Description',
        'Selling Price (RM)',
        'Purchase Price (RM)',
        'Brand',
        'Quantity',
        'Product Type',
        'Barcode',
        'Variant Options', // e.g., Size: M | Color: RED
        'Variant Price Delta',
      ];
      
      const productRows = [];
      
      for (const product of products) {
        try {
          productRows.push([
            'product',
            '',
            product.sku || '',
            product.name || '',
            product.desc || '',
            product.price || 0,
            product.purchase || 0,
            product.brand || '',
            product.qty || 0,
            product.type || '',
            product.barcode || '',
            '',
            '',
          ]);

          // variants
          (product.variants || []).forEach((variant) => {
            const options = (variant.options || [])
              .map((o) => `${o.group}: ${o.value}`)
              .join(' | ');
            productRows.push([
              'variant',
              product.sku || '',
              variant.sku || '',
              product.name || '',
              product.desc || '',
              (Number(product.price || 0) + Number(variant.priceDelta || 0)).toFixed(2),
              product.purchase || 0,
              product.brand || '',
              variant.qty || 0,
              product.type || '',
              variant.barcode || '',
              options,
              variant.priceDelta || 0,
            ]);
          });
          
        } catch (error) {
          console.error(`Error processing product ${product.id || 'unknown'}:`, error);
        }
      }
      
      if (productRows.length > 0) {
        downloadCSV(
          productRows,
          productHeaders,
          `products_export_${timestamp}.csv`
        );
      }
      
    } catch (error) {
      console.error('Error exporting products:', error);
      setExportError('Failed to export products. ' + (error.message || ''));
      throw error; // Re-throw to be caught by the main try-catch
    }
  };

  const exportJournal = async (timestamp) => {
    try {
      const entriesRef = collection(db, 'companies', companyId, 'journalEntries');
      const q = query(entriesRef, orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const rows = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.entries) && data.entries.length > 0) {
          const date = data.date?.toDate ? data.date.toDate().toISOString().split('T')[0] : data.date?.split('T')[0] || '';
          let isFirstEntry = true;
          
          data.entries.forEach((entry, index) => {
            rows.push([
              isFirstEntry ? date : '',
              isFirstEntry ? (data.reference || '') : '',
              isFirstEntry ? (data.description || '') : '',
              entry.account || '',
              entry.debit ? parseFloat(entry.debit).toFixed(2) : '0.00',
              entry.credit ? parseFloat(entry.credit).toFixed(2) : '0.00',
              entry.notes || ''
            ]);
            isFirstEntry = false;
          });
          
          // Add an empty row after each transaction for better readability
          if (data.entries.length > 0) {
            rows.push(Array(7).fill(''));
          }
        }
      });

      // Remove the last empty row if it exists
      if (rows.length > 0 && rows[rows.length - 1].every(cell => cell === '')) {
        rows.pop();
      }

      if (rows.length === 0) {
        setExportError('No journal entries found to export');
        return;
      }

      const headers = ['Date', 'Reference', 'Description', 'Account', 'Debit (RM)', 'Credit (RM)', 'Notes'];
      downloadCSV(rows, headers, `journal_entries_${timestamp}.csv`);
    } catch (error) {
      console.error('Error exporting journal entries:', error);
      throw new Error('Failed to export journal entries');
    }
  };

  const exportMoneyIn = async (timestamp) => {
    try {
      const moneyInRef = collection(db, 'companies', companyId, 'moneyIn');
      const q = query(moneyInRef, orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const transactions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        transactions.push({
          id: doc.id,
          date: data.date?.toDate ? data.date.toDate().toISOString().split('T')[0] : data.date?.split('T')[0] || '',
          reference: data.reference || '',
          description: data.description || '',
          amount: data.amount || 0,
          category: data.category || '',
          paymentMethod: data.paymentMethod || '',
          account: data.account || 'cash',
          contactName: data.contact?.company || data.contact?.name,
        });
      });

      if (transactions.length === 0) {
        setExportError('No money in transactions found to export');
        return;
      }

      const headers = ['Date', 'Reference', 'Description', 'Contact Name', 'Category', 'Payment Method', 'Deposit To', 'Amount (RM)'];
      const rows = transactions.map(tx => [
        tx.date,
        tx.reference,
        tx.description,
        tx.contactName,
        tx.category,
        tx.paymentMethod,
        tx.account,
        tx.amount,
      ]);

      downloadCSV(rows, headers, `money_in_${timestamp}.csv`);
    } catch (error) {
      console.error('Error exporting money in transactions:', error);
      throw new Error('Failed to export money in transactions');
    }
  };

  const exportMoneyOut = async (timestamp) => {
    try {
      const moneyOutRef = collection(db, 'companies', companyId, 'moneyOut');
      const q = query(moneyOutRef, orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const transactions = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        transactions.push({
          date: data.date?.toDate ? data.date.toDate().toISOString().split('T')[0] : data.date?.split('T')[0] || '',
          reference: data.reference || '',
          description: data.description || '',
          amount: data.amount || 0,
          category: data.category || '',
          paymentMethod: data.paymentMethod || '',
          account: data.account || 'expenses',
          contactName: data.contact?.company || data.contact?.name,
        });
      });

      if (transactions.length === 0) {
        setExportError('No money out transactions found to export');
        return;
      }

      const headers = ['Date', 'Reference', 'Description', 'Payee', 'Category', 'Payment Method', 'Account', 'Amount (RM)'];
      const rows = transactions.map(tx => [
        tx.date,
        tx.reference,
        tx.description,
        tx.contactName,
        tx.category,
        tx.paymentMethod,
        tx.account,
        tx.amount,
      ]);

      downloadCSV(rows, headers, `money_out_${timestamp}.csv`);
    } catch (error) {
      console.error('Error exporting money out transactions:', error);
      throw new Error('Failed to export money out transactions');
    }
  };

  const exportBundles = async (timestamp) => {
    try {
      const bundlesRef = collection(db, 'companies', companyId, 'inventory', 'data', 'bundles');
      const snap = await getDocs(bundlesRef);
      if (snap.empty) {
        setExportError('No bundles found to export');
        return;
      }
      const headers = ['Bundle Name', 'SKU', 'Price (RM)', 'Description', 'Items JSON'];
      const rows = snap.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        const items = (data.items || []).map((it) => ({
          sku: it.sku || '',
          quantity: it.quantity || 0,
          variantId: it.variantId || '',
        }));
        return [
          data.name || '',
          data.sku || '',
          data.price || 0,
          data.desc || '',
          JSON.stringify(items),
        ];
      });
      downloadCSV(rows, headers, `bundles_${timestamp}.csv`);
    } catch (error) {
      console.error('Error exporting bundles:', error);
      throw new Error('Failed to export bundles');
    }
  };

  const downloadTemplate = (type) => {
    let headers = [];
    let filename = '';
    let exampleData = [];

    switch (type) {
      case 'products':
        headers = [
          'Record Type', 'Parent SKU', 'SKU', 'Product Name', 'Description', 'Selling Price (RM)', 
          'Purchase Price (RM)', 'Brand', 'Quantity', 'Product Type', 'Barcode', 'Variant Options', 'Variant Price Delta'
        ];
        exampleData = [
          ['product', '', 'SKU001', 'Shirt', 'Cotton shirt', '19.99', '9.99', 'Brand A', '100', 'CLOTHING', '1234567890123', '', ''],
          ['variant', 'SKU001', 'SKU001-RED-M', 'Shirt', 'Cotton shirt', '19.99', '9.99', 'Brand A', '20', 'CLOTHING', '1234567890999', 'Color: RED | Size: M', '0'],
          ['variant', 'SKU001', 'SKU001-BLU-L', 'Shirt', 'Cotton shirt', '21.99', '9.99', 'Brand A', '10', 'CLOTHING', '1234567890888', 'Color: BLUE | Size: L', '2']
        ];
        filename = 'products_import_template.csv';
        break;
      case 'bundles':
        headers = ['Bundle Name', 'SKU', 'Price (RM)', 'Description', 'Items JSON'];
        exampleData = [
          ['Starter Pack', 'BND-001', '49.99', 'Bundle of basics', '[{"sku":"SKU001","quantity":1},{"sku":"SKU002","quantity":2}]']
        ];
        filename = 'bundles_import_template.csv';
        break;

      case 'journal':
        headers = ['Date', 'Reference', 'Description', 'Account', 'Debit (RM)', 'Credit (RM)', 'Notes'];
        exampleData = [
          ['2025-09-10', 'INV001', 'Sales invoice', 'Accounts Receivable', '100.00', '0.00', 'Invoice #001'],
          ['', '', '', 'Sales Revenue', '0.00', '100.00', '']
        ];
        filename = 'journal_import_template.csv';
        break;

      case 'money_in':
        headers = ['Date', 'Reference', 'Description', 'Contact Name', 'Category', 'Payment Method', 'Deposit To', 'Amount (RM)'];
        exampleData = [
          ['2025-09-10', 'INV001', 'Product sale', 'ABC Corp', 'Sales', 'Credit Card', 'Business Account', '100.00']
        ];
        filename = 'money_in_import_template.csv';
        break;

      case 'money_out':
        headers = ['Date', 'Reference', 'Description', 'Payee', 'Category', 'Payment Method', 'Account', 'Amount (RM)'];
        exampleData = [
          ['2025-09-10', 'BILL001', 'Office supplies', 'Office Mart', 'Office Expenses', 'Bank Transfer', 'Business Account', '50.00']
        ];
        filename = 'money_out_import_template.csv';
        break;

      default:
        return;
    }

    // Add headers to the data
    const csvData = [headers, ...exampleData];
    
    // Convert to CSV
    const csvContent = csvData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getImportTemplateButton = () => {
    const templateTypes = {
      'products': 'products',
      'bundles': 'bundles',
      'journal': 'journal',
      'moneyIn': 'money_in',
      'moneyOut': 'money_out'
    };

    const currentType = templateTypes[importType];
    const buttonText = {
      'products': 'Products',
      'bundles': 'Bundles',
      'journal': 'Journal Entries',
      'money_in': 'Money In',
      'money_out': 'Money Out'
    }[currentType];

    return (
      <button
        onClick={() => downloadTemplate(currentType)}
        className="mt-4 w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
      >
        <FiDownload className="-ml-1 mr-2 h-5 w-5" />
        Download {buttonText} Template
      </button>
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvData = event.target.result;
        const lines = csvData.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        
        const data = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          if (values.length !== headers.length) {
            errors.push(`Row ${i + 1}: Incorrect number of columns`);
            continue;
          }
          
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index];
            const lowerKey = header.toLowerCase();
            if (!(lowerKey in row)) {
              row[lowerKey] = values[index];
            }
          });
          
          // Basic validation
          if (importType === 'products' || importType === 'bundles') {
            // Skip strict validation here; handled in import handlers when implemented
          } else if (importType === 'journal') {
            if (!row['Date'] || !row['Reference'] || !row['Entries']) {
              errors.push(`Row ${i + 1}: Date, Reference, and Entries are required`);
            }
          } else if (importType === 'moneyIn' || importType === 'moneyOut') {
            if (!row['Date'] || !row['Reference'] || !row['Amount'] || !row['Category']) {
              errors.push(`Row ${i + 1}: Date, Reference, Amount, and Category are required`);
            }
            if (isNaN(row['Amount'])) {
              errors.push(`Row ${i + 1}: Amount must be a number`);
            }
          }
          
          data.push(row);
        }
        
        setImportData(data);
        setValidationErrors(errors);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        setImportStatus({ success: false, message: 'Error parsing CSV file' });
      }
    };
    
    reader.readAsText(file);
    setImportFile(file);
  };

  const validateRequiredFields = (row, requiredFields, rowNumber) => {
    const missingFields = requiredFields.filter(field => !row[field]);
    if (missingFields.length > 0) {
      return `Row ${rowNumber}: Missing required fields: ${missingFields.join(', ')}`;
    }
    return null;
  };

  const importJournalEntries = async (data) => {
    const entriesRef = collection(db, 'companies', companyId, 'journalEntries');
    
    try {
      // Get existing references for duplicate checking
      const existingRefs = [...new Set(data.map(row => row.reference).filter(Boolean))];
      let existingEntries = [];
      
      if (existingRefs.length > 0) {
        const q = query(entriesRef, where('reference', 'in', existingRefs));
        const querySnapshot = await getDocs(q);
        existingEntries = querySnapshot.docs.map(doc => doc.data().reference);
      }
      
      let importedCount = 0;
      const errors = [];
      let currentBatch = writeBatch(db);
      let batchSize = 0;
      
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNumber = i + 2; // +2 for header row and 1-based index
        
        try {
          // Validate required fields
          const requiredError = validateRequiredFields(
            row, 
            ['date', 'reference', 'account', 'debit', 'credit'], 
            rowNumber
          );
          
          if (requiredError) {
            errors.push(requiredError);
            continue;
          }
          
          // Check for duplicate reference
          if (existingEntries.includes(row.reference)) {
            errors.push(`Row ${rowNumber}: Journal entry with reference "${row.reference}" already exists`);
            continue;
          }
          
          // Parse amounts
          const debit = parseFloat(row.debit) || 0;
          const credit = parseFloat(row.credit) || 0;
          
          if (isNaN(debit) || isNaN(credit)) {
            errors.push(`Row ${rowNumber}: Invalid debit or credit amount`);
            continue;
          }
          
          // Create the document
          const docRef = doc(entriesRef);
          currentBatch.set(docRef, {
            date: Timestamp.fromDate(new Date(row.date)),
            reference: row.reference,
            description: row.description || '',
            account: row.account,
            debit: debit,
            credit: credit,
            notes: row.notes || '',
            createdAt: serverTimestamp(),
            createdBy: user.uid,
            updatedAt: serverTimestamp(),
            updatedBy: user.uid
          });
          
          importedCount++;
          batchSize++;
          
          // Commit in chunks
          if (batchSize >= 400) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            batchSize = 0;
          }
          
        } catch (error) {
          console.error(`Error processing row ${rowNumber}:`, error);
          errors.push(`Row ${rowNumber}: ${error.message}`);
        }
      }
      
      // Commit any remaining operations
      if (batchSize > 0) {
        await currentBatch.commit();
      }
      
      return { success: true, imported: importedCount, errors };
      
    } catch (error) {
      console.error('Error importing journal entries:', error);
      return { success: false, error: error.message };
    }
  };

  const importMoneyIn = async (data) => {
    const moneyInRef = collection(db, 'companies', companyId, 'moneyIn');
    
    try {
      let importedCount = 0;
      const errors = [];
      let currentBatch = writeBatch(db);
      let batchSize = 0;
      
      // Get existing references for duplicate checking
      const existingRefs = [...new Set(data.map(row => row.reference).filter(Boolean))];
      let existingEntries = [];
      
      if (existingRefs.length > 0) {
        const q = query(moneyInRef, where('reference', 'in', existingRefs));
        const querySnapshot = await getDocs(q);
        existingEntries = querySnapshot.docs.map(doc => doc.data().reference);
      }
      
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNumber = i + 2;
        
        try {
          // Validate required fields
          const requiredError = validateRequiredFields(
            row,
            ['date', 'reference', 'amount', 'category', 'paymentMethod', 'account'],
            rowNumber
          );
          
          if (requiredError) {
            errors.push(requiredError);
            continue;
          }
          
          // Check for duplicate reference
          if (existingEntries.includes(row.reference)) {
            errors.push(`Row ${rowNumber}: Money In with reference "${row.reference}" already exists`);
            continue;
          }
          
          // Parse amount
          const amount = parseFloat(row.amount);
          if (isNaN(amount) || amount <= 0) {
            errors.push(`Row ${rowNumber}: Invalid amount`);
            continue;
          }
          
          // Create the document
          const docRef = doc(moneyInRef);
          currentBatch.set(docRef, {
            date: Timestamp.fromDate(new Date(row.date)),
            reference: row.reference,
            description: row.description || '',
            amount: amount,
            category: row.category,
            paymentMethod: row.paymentMethod,
            account: row.account,
            contactName: row.contactName || '',
            status: 'completed',
            createdAt: serverTimestamp(),
            createdBy: user.uid,
            updatedAt: serverTimestamp(),
            updatedBy: user.uid
          });
          
          importedCount++;
          batchSize++;
          
          // Commit in chunks
          if (batchSize >= 400) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            batchSize = 0;
          }
          
        } catch (error) {
          console.error(`Error processing row ${rowNumber}:`, error);
          errors.push(`Row ${rowNumber}: ${error.message}`);
        }
      }
      
      // Commit any remaining operations
      if (batchSize > 0) {
        await currentBatch.commit();
      }
      
      return { success: true, imported: importedCount, errors };
      
    } catch (error) {
      console.error('Error importing money in transactions:', error);
      return { success: false, error: error.message };
    }
  };

  const importMoneyOut = async (data) => {
    const moneyOutRef = collection(db, 'companies', companyId, 'moneyOut');
    
    try {
      let importedCount = 0;
      const errors = [];
      let currentBatch = writeBatch(db);
      let batchSize = 0;
      
      // Get existing references for duplicate checking
      const existingRefs = [...new Set(data.map(row => row.reference).filter(Boolean))];
      let existingEntries = [];
      
      if (existingRefs.length > 0) {
        const q = query(moneyOutRef, where('reference', 'in', existingRefs));
        const querySnapshot = await getDocs(q);
        existingEntries = querySnapshot.docs.map(doc => doc.data().reference);
      }
      
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNumber = i + 2;
        
        try {
          // Validate required fields
          const requiredError = validateRequiredFields(
            row,
            ['date', 'reference', 'amount', 'category', 'paymentMethod', 'account'],
            rowNumber
          );
          
          if (requiredError) {
            errors.push(requiredError);
            continue;
          }
          
          // Check for duplicate reference
          if (existingEntries.includes(row.reference)) {
            errors.push(`Row ${rowNumber}: Money Out with reference "${row.reference}" already exists`);
            continue;
          }
          
          // Parse amount
          const amount = parseFloat(row.amount);
          if (isNaN(amount) || amount <= 0) {
            errors.push(`Row ${rowNumber}: Invalid amount`);
            continue;
          }
          
          // Create the document
          const docRef = doc(moneyOutRef);
          currentBatch.set(docRef, {
            date: Timestamp.fromDate(new Date(row.date)),
            reference: row.reference,
            description: row.description || '',
            amount: amount,
            category: row.category,
            paymentMethod: row.paymentMethod,
            account: row.account,
            contactName: row.contactName || '',
            status: 'completed',
            createdAt: serverTimestamp(),
            createdBy: user.uid,
            updatedAt: serverTimestamp(),
            updatedBy: user.uid
          });
          
          importedCount++;
          batchSize++;
          
          // Commit in chunks
          if (batchSize >= 400) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            batchSize = 0;
          }
          
        } catch (error) {
          console.error(`Error processing row ${rowNumber}:`, error);
          errors.push(`Row ${rowNumber}: ${error.message}`);
        }
      }
      
      // Commit any remaining operations
      if (batchSize > 0) {
        await currentBatch.commit();
      }
      
      return { success: true, imported: importedCount, errors };
      
    } catch (error) {
      console.error('Error importing money out transactions:', error);
      return { success: false, error: error.message };
    }
  };

  const parseVariantOptions = (optionsStr = '') => {
    return optionsStr
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((segment) => {
        const [group, value] = segment.split(':').map((s) => (s || '').trim());
        if (!group || !value) return null;
        return { group, value };
      })
      .filter(Boolean);
  };

  const importProductsData = async (rows) => {
    const productsRef = collection(db, 'companies', companyId, 'inventory', 'data', 'products');
    const productTypesRef = collection(db, 'companies', companyId, 'companySettings', 'inventory', 'productTypes');
    const typesSnap = await getDocs(productTypesRef);
    const existingTypes = new Set(typesSnap.docs.map((d) => (d.data().name || '').toUpperCase()));
    const ensureProductType = async (typeName) => {
      const upper = (typeName || '').toUpperCase();
      if (!upper) return '';
      if (existingTypes.has(upper)) return upper;
      await addDoc(productTypesRef, { name: upper });
      existingTypes.add(upper);
      return upper;
    };

    const existingSnap = await getDocs(productsRef);
    const existingSkuMap = new Map();
    const existingVariantSkuMap = new Map();

    existingSnap.docs.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const sku = (data.sku || '').trim();
      if (sku) existingSkuMap.set(sku, docSnap.id);
      (data.variants || []).forEach((v) => {
        if (v.sku) existingVariantSkuMap.set(v.sku.trim(), { productId: docSnap.id, variantId: v.id });
      });
    });

    const grouped = {};
    const errors = [];
    rows.forEach((row, idx) => {
      const recordType = (row['Record Type'] || row['record type'] || '').toLowerCase();
      if (!recordType || recordType === 'product') {
        const sku = (row['SKU'] || row['sku'] || '').trim();
        if (!sku) {
          errors.push(`Row ${idx + 2}: Missing SKU for product row`);
          return;
        }
        grouped[sku] = grouped[sku] || { base: null, variants: [] };
        grouped[sku].base = row;
      } else if (recordType === 'variant') {
        const parent = (row['Parent SKU'] || row['parent sku'] || '').trim();
        if (!parent) {
          errors.push(`Row ${idx + 2}: Variant missing Parent SKU`);
          return;
        }
        grouped[parent] = grouped[parent] || { base: null, variants: [] };
        grouped[parent].variants.push(row);
      } else {
        errors.push(`Row ${idx + 2}: Unknown Record Type "${recordType}"`);
      }
    });

    let importedCount = 0;
    let batch = writeBatch(db);
    let batchSize = 0;

    for (const sku of Object.keys(grouped)) {
      if (existingSkuMap.has(sku)) {
        errors.push(`SKU ${sku} already exists; skipped`);
        continue;
      }

      const { base, variants } = grouped[sku];
      if (!base) {
        errors.push(`No base product row for SKU ${sku}`);
        continue;
      }

      const baseName = base['Product Name'] || base['Name'] || '';
      if (!baseName) {
        errors.push(`SKU ${sku}: Product Name is required`);
        continue;
      }

      const basePrice = parseFloat(base['Selling Price (RM)'] || base['Price'] || 0) || 0;
      const purchase = parseFloat(base['Purchase Price (RM)'] || base['Purchase Price'] || 0) || 0;
      const brand = base['Brand'] || '';
      const qtyBase = parseFloat(base['Quantity'] || 0) || 0;
      const type = await ensureProductType(base['Product Type'] || '');
      const barcode = base['Barcode'] || '';
      const desc = base['Description'] || '';

      const variantDocs = [];
      const optionsMap = new Map(); // group -> Set(values)
      let variantsQtySum = 0;
      let hasVariantError = false;

      variants.forEach((vRow, idx) => {
        const vSku = (vRow['SKU'] || '').trim();
        if (!vSku) {
          errors.push(`Variant row under ${sku} missing SKU (row ${idx + 2})`);
          hasVariantError = true;
          return;
        }
        if (existingVariantSkuMap.has(vSku) || existingSkuMap.has(vSku)) {
          errors.push(`Variant SKU ${vSku} already exists; skipped`);
          hasVariantError = true;
          return;
        }
        const vPriceDelta = parseFloat(vRow['Variant Price Delta'] || 0) || 0;
        const vQty = parseFloat(vRow['Quantity'] || 0) || 0;
        const vBarcode = vRow['Barcode'] || '';
        const options = parseVariantOptions(vRow['Variant Options'] || vRow['Options'] || '');
        options.forEach((opt) => {
          const group = (opt.group || '').toUpperCase();
          const value = (opt.value || '').toUpperCase();
          if (!group || !value) return;
          const set = optionsMap.get(group) || new Set();
          set.add(value);
          optionsMap.set(group, set);
        });
        variantsQtySum += vQty;
        variantDocs.push({
          id: uuidv4(),
          options: options.map((o) => ({ group: (o.group || '').toUpperCase(), value: (o.value || '').toUpperCase() })),
          priceDelta: vPriceDelta,
          price: basePrice + vPriceDelta,
          qty: vQty,
          barcode: vBarcode,
          sku: vSku,
        });
      });

      if (hasVariantError) continue;

      const options = Array.from(optionsMap.entries()).map(([group, set]) => ({
        name: group,
        values: Array.from(set),
      }));

      const productQty = variantDocs.length ? variantsQtySum : qtyBase;

      const docRef = doc(productsRef);
      batch.set(docRef, {
        sku,
        baseSku: sku,
        name: baseName,
        desc,
        price: basePrice,
        purchase,
        brand,
        qty: productQty,
        type,
        barcode,
        hasVariants: variantDocs.length > 0,
        variants: variantDocs,
        options,
        trackInventory: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      importedCount += 1;
      batchSize += 1;
      if (batchSize >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        batchSize = 0;
      }
    }

    if (batchSize > 0) {
      await batch.commit();
    }

    return { success: true, imported: importedCount, errors };
  };

  const importBundles = async (rows) => {
    const bundlesRef = collection(db, 'companies', companyId, 'inventory', 'data', 'bundles');
    const productsRef = collection(db, 'companies', companyId, 'inventory', 'data', 'products');
    const productSnap = await getDocs(productsRef);
    const productSkuMap = new Map();
    const variantSkuMap = new Map();
    productSnap.docs.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const sku = (data.sku || '').trim();
      if (sku) productSkuMap.set(sku, { id: docSnap.id, data });
      (data.variants || []).forEach((v) => {
        if (v.sku) variantSkuMap.set(v.sku.trim(), { productId: docSnap.id, variant: v, product: data });
      });
    });

    const existingBundlesSnap = await getDocs(bundlesRef);
    const existingBundleSku = new Set(existingBundlesSnap.docs.map((d) => (d.data().sku || '').trim()).filter(Boolean));

    const errors = [];
    let importedCount = 0;
    let batch = writeBatch(db);
    let batchSize = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 2;
      const name = (row['Bundle Name'] || '').trim();
      const sku = (row['SKU'] || '').trim();
      const price = parseFloat(row['Price (RM)'] || row['Price'] || 0) || 0;
      const desc = row['Description'] || '';
      const itemsJson = row['Items JSON'] || row['Items'] || '';

      if (!name || !sku || !itemsJson) {
        errors.push(`Row ${rowNumber}: Bundle Name, SKU, and Items JSON are required`);
        continue;
      }

      if (existingBundleSku.has(sku)) {
        errors.push(`Row ${rowNumber}: Bundle SKU ${sku} already exists`);
        continue;
      }

      let itemsParsed = [];
      try {
        itemsParsed = JSON.parse(itemsJson);
        if (!Array.isArray(itemsParsed)) throw new Error('Items JSON must be an array');
      } catch (e) {
        errors.push(`Row ${rowNumber}: Invalid Items JSON`);
        continue;
      }

      const resolvedItems = [];
      let itemError = false;
      for (const it of itemsParsed) {
        const itemSku = (it.sku || '').trim();
        const qty = parseFloat(it.quantity || it.qty || 0) || 0;
        if (!itemSku || qty <= 0) {
          errors.push(`Row ${rowNumber}: Invalid item entry (sku/quantity)`);
          itemError = true;
          break;
        }
        if (variantSkuMap.has(itemSku)) {
          const entry = variantSkuMap.get(itemSku);
          const variantName = (entry.variant.options || []).map((o) => `${o.group}: ${o.value}`).join(', ');
          resolvedItems.push({
            id: entry.productId,
            variantId: entry.variant.id,
            sku: itemSku,
            name: entry.product?.name || '',
            variantName,
            quantity: qty,
          });
        } else if (productSkuMap.has(itemSku)) {
          const entry = productSkuMap.get(itemSku);
          resolvedItems.push({
            id: entry.id,
            sku: itemSku,
            name: entry.data?.name || '',
            quantity: qty,
          });
        } else {
          errors.push(`Row ${rowNumber}: Item SKU ${itemSku} not found`);
          itemError = true;
          break;
        }
      }
      if (itemError) continue;

      const docRef = doc(bundlesRef);
      batch.set(docRef, {
        name,
        sku,
        price,
        desc,
        items: resolvedItems,
        quantity: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      importedCount += 1;
      batchSize += 1;
      if (batchSize >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        batchSize = 0;
      }
    }

    if (batchSize > 0) {
      await batch.commit();
    }

    return { success: true, imported: importedCount, errors };
  };

  const processImport = async () => {
    if (!importFile) {
      setImportStatus({ success: false, message: 'Please select a file to import' });
      return;
    }
    
    setIsExporting(true);
    setImportStatus({ success: null, message: 'Validating file...' });
    setValidationErrors([]);
    
    try {
      const fileData = await readFile(importFile);
      const result = parseCSV(fileData);
      
      if (result.length === 0) {
        throw new Error('The file is empty or could not be parsed');
      }
      
      // Basic validation based on import type
      const requiredFields = {
        'products': ['SKU', 'Product Name'],
        'bundles': ['Bundle Name', 'Items JSON'],
        'journal': ['date', 'reference', 'account', 'debit', 'credit'],
        'moneyIn': ['date', 'reference', 'amount', 'category', 'paymentMethod', 'account'],
        'moneyOut': ['date', 'reference', 'amount', 'category', 'paymentMethod', 'account']
      };
      
      const missingFields = [];
      const headers = Object.keys(result[0] || {}).map(h => h.toLowerCase());
      
      if (requiredFields[importType]) {
        requiredFields[importType].forEach(field => {
          if (!headers.includes(field.toLowerCase())) {
            missingFields.push(field);
          }
        });
      }
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required columns: ${missingFields.join(', ')}`);
      }
      
      setImportStatus({ success: null, message: 'Importing data...' });
      
      let importResult;
      
      switch (importType) {
        case 'journal':
          importResult = await importJournalEntries(result);
          break;
        case 'moneyIn':
          importResult = await importMoneyIn(result);
          break;
        case 'moneyOut':
          importResult = await importMoneyOut(result);
          break;
        case 'products':
          importResult = await importProductsData(result);
          break;
        case 'bundles':
          importResult = await importBundles(result);
          break;
        default:
          throw new Error('Unsupported import type');
      }
      
      if (importResult.success) {
        const successMessage = `Successfully imported ${importResult.imported} ${importType} record(s)`;
        const errorMessages = importResult.errors?.length > 0 
          ? ` with ${importResult.errors.length} error(s)` 
          : '';
          
        setImportStatus({ 
          success: true, 
          message: `${successMessage}${errorMessages}`,
          imported: importResult.imported,
          errors: importResult.errors
        });
        
        // Show validation errors if any
        if (importResult.errors?.length > 0) {
          setValidationErrors(importResult.errors);
        }
      } else {
        throw new Error(importResult.error || 'Import failed');
      }
      
    } catch (error) {
      console.error('Import error:', error);
      setImportStatus({ 
        success: false, 
        message: `Error during import: ${error.message}` 
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 text-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl text-white font-bold mb-6">Data Backup & Restore</h2>
        
        <div className="border-b border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('export')}
              className={`${
                activeTab === 'export' 
                  ? 'border-purple-500 text-purple-400' 
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-400'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Export Data
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`${
                activeTab === 'import' 
                  ? 'border-purple-500 text-purple-400' 
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-400'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Import Data
            </button>
          </nav>
        </div>
        
        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="bg-gray-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold text-gray-100 mb-6">
              Export {exportType === 'all' ? 'All Data' : exportType.charAt(0).toUpperCase() + exportType.slice(1)}
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Exports are CSV files. Products include variants and barcodes; bundles include their component SKUs and quantities; finance exports cover journal entries, money in, and money out.
            </p>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Select Data to Export</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                <button
                  onClick={() => setExportType('products')}
                  className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                    exportType === 'products' ? 'border-purple-500 bg-purple-900/30' : 'border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <FiPackage className="w-6 h-6 text-purple-400 mb-2" />
                  <span className="text-gray-200">Products</span>
                </button>
                
                <button
                  onClick={() => setExportType('journal')}
                  className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                    exportType === 'journal' ? 'border-purple-500 bg-purple-900/30' : 'border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <FiClock className="w-6 h-6 text-blue-400 mb-2" />
                  <span className="text-gray-200">Journal Entries</span>
                </button>
                
                <button
                  onClick={() => setExportType('moneyIn')}
                  className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                    exportType === 'moneyIn' ? 'border-purple-500 bg-purple-900/30' : 'border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <FiUpload className="w-6 h-6 text-green-400 mb-2" />
                  <span className="text-gray-200">Money In</span>
                </button>
                
                <button
                  onClick={() => setExportType('moneyOut')}
                  className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                    exportType === 'moneyOut' ? 'border-purple-500 bg-purple-900/30' : 'border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <FiDownload className="w-6 h-6 text-red-400 mb-2" />
                  <span className="text-gray-200">Money Out</span>
                </button>

                <button
                  onClick={() => setExportType('bundles')}
                  className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                    exportType === 'bundles' ? 'border-purple-500 bg-purple-900/30' : 'border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <FiPackage className="w-6 h-6 text-cyan-400 mb-2" />
                  <span className="text-gray-200">Bundles</span>
                </button>
                
                <button
                  onClick={() => setExportType('all')}
                  className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                    exportType === 'all' ? 'border-purple-500 bg-purple-900/30' : 'border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <FiCloud className="w-6 h-6 text-gray-400 mb-2" />
                  <span className="text-gray-200">All Data</span>
                </button>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <button
                onClick={exportData}
                disabled={isExporting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                  isExporting 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {isExporting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <FiFileText className="w-5 h-5" />
                    {`Export ${exportType === 'all' ? 'All Data' : exportType.charAt(0).toUpperCase() + exportType.slice(1)}`}
                  </>
                )}
              </button>
              
              {exportSuccess && (
                <div className="flex items-center text-green-400 text-sm">
                  <FiCheckCircle className="w-5 h-5 mr-1" />
                  {exportSuccess}
                </div>
              )}
              
              {exportError && (
                <div className="text-red-400 text-sm">
                  {exportError}
                </div>
              )}
              
              {lastExportTime && (
                <div className="text-sm text-gray-300 mt-2 sm:mt-0 sm:ml-4">
                  Last exported: {lastExportTime}
                </div>
              )}
            </div>
            
            <div className="mt-6 p-4 bg-gray-800 rounded-lg">
              <h3 className="font-medium text-gray-100 mb-2">
                {exportType === 'products' ? 'Products Export Includes:' : 
                 exportType === 'contacts' ? 'Contacts Export Includes:' : 
                 exportType === 'journal' ? 'Journal Entries Export Includes:' : 
                 exportType === 'moneyIn' ? 'Money In Export Includes:' : 
                 exportType === 'moneyOut' ? 'Money Out Export Includes:' : 'Export Includes:'}
              </h3>
              
              {(exportType === 'products' || exportType === 'all') && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Products:</h4>
                  <ul className="text-sm text-gray-300 space-y-1 pl-4">
                    <li>• SKU</li>
                    <li>• Product Name</li>
                    <li>• Description</li>
                    <li>• Selling Price (RM)</li>
                    <li>• Purchase Price (RM)</li>
                    <li>• Brand</li>
                    <li>• Quantity</li>
                    <li>• Product Type</li>
                    <li>• Barcode</li>
                    <li>• Variants (record type, parent SKU, variant SKU, options, qty, price delta)</li>
                  </ul>
                </div>
              )}
              {(exportType === 'bundles' || exportType === 'all') && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Bundles:</h4>
                  <ul className="text-sm text-gray-300 space-y-1 pl-4">
                    <li>• Bundle name and SKU</li>
                    <li>• Price (RM) and description</li>
                    <li>• Items JSON (SKU + quantity per item)</li>
                  </ul>
                </div>
              )}
              
              {(exportType === 'journal' || exportType === 'all') && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Journal Entries:</h4>
                  <ul className="text-sm text-gray-300 space-y-1 pl-4">
                    <li>• Date</li>
                    <li>• Reference</li>
                    <li>• Description</li>
                    <li>• Entries</li>
                  </ul>
                </div>
              )}
              
              {(exportType === 'moneyIn' || exportType === 'all') && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Money In:</h4>
                  <ul className="text-sm text-gray-300 space-y-1 pl-4">
                    <li>• Date</li>
                    <li>• Reference</li>
                    <li>• Description</li>
                    <li>• Contact</li>
                    <li>• Category</li>
                    <li>• Payment Method</li>
                    <li>• Deposit To</li>
                    <li>• Amount (RM)</li>
                  </ul>
                </div>
              )}
              
              {(exportType === 'moneyOut' || exportType === 'all') && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-1">Money Out:</h4>
                  <ul className="text-sm text-gray-300 space-y-1 pl-4">
                    <li>• Date</li>
                    <li>• Reference</li>
                    <li>• Description</li>
                    <li>• Payee</li>
                    <li>• Category</li>
                    <li>• Payment Method</li>
                    <li>• Account</li>
                    <li>• Amount (RM)</li>
                  </ul>
                </div>
              )}
              
            </div>
          </div>
        )}
        
        {/* Import Tab */}
        {activeTab === 'import' && (
          <div className="bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-100 mb-6">
              Import {importType.charAt(0).toUpperCase() + importType.slice(1)}
            </h2>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Select Data to Import</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setImportType('products')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    importType === 'products' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Products
                </button>
                <button
                  onClick={() => setImportType('journal')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    importType === 'journal' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Journal Entries
                </button>
                <button
                  onClick={() => setImportType('bundles')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    importType === 'bundles' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Bundles
                </button>
                <button
                  onClick={() => setImportType('moneyIn')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    importType === 'moneyIn' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Money In
                </button>
                <button
                  onClick={() => setImportType('moneyOut')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    importType === 'moneyOut' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  Money Out
                </button>
              </div>
              
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Step To Import Data</h3>
                
                <div className="space-y-4">
                  {/* Step 1: Download Template */}
                  <div>
                    <p className="text-sm font-medium text-gray-300 mb-2">1. Download template</p>
                    <p className="text-xs text-gray-400 mb-2">
                      Download the template file and fill in your data. Make sure to follow the format exactly.
                    </p>
                    {getImportTemplateButton()}
                  </div>

                  {/* Step 2: Upload File */}
                  <div>
                    <p className="text-sm font-medium text-gray-300 mb-2">2. Upload your file</p>
                    <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-600 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm">
                          <label
                            htmlFor="file-upload"
                            className="relative cursor-pointer bg-transparent rounded-md font-medium text-purple-400 hover:text-purple-300 focus-within:outline-none"
                          >
                            <span>Upload a file</span>
                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                              accept=".csv, .xlsx, .xls"
                              onChange={handleFileUpload}
                              ref={fileInputRef}
                            />
                          </label>
                          <p className="pl-1 text-gray-400">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          CSV, XLS, XLSX files only, up to 10MB
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {importFile && (
                <div className="mt-2 text-sm text-green-400 flex items-center justify-center">
                  <FiCheckCircle className="w-4 h-4 mr-1" />
                  {importFile.name}
                </div>
              )}
              
              {validationErrors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-md">
                  <div className="flex">
                    <FiAlertTriangle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        {validationErrors.length} validation error{validationErrors.length > 1 ? 's' : ''} found
                      </h3>
                      <div className="mt-2 text-sm text-red-700 max-h-40 overflow-y-auto">
                        <ul className="list-disc pl-5 space-y-1">
                          {validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {importStatus.message && (
                <div className={`mt-4 p-4 rounded-md ${
                  importStatus.success 
                    ? 'bg-green-900/30 border border-green-800' 
                    : 'bg-red-900/30 border border-red-800'
                }`}>
                  <div className="flex">
                    {importStatus.success ? (
                      <FiCheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <FiAlertTriangle className="h-5 w-5 text-red-400" />
                    )}
                    <div className="ml-3">
                      <p className="text-sm">{importStatus.message}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6">
                <button
                  type="button"
                  onClick={processImport}
                  disabled={!importFile || isExporting}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md ${
                    !importFile || isExporting
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                  } w-full sm:w-auto`}
                >
                  {isExporting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importing...
                    </>
                  ) : 'Import Data'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Backup;
